import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  confirmPasswordRecovery,
  requestPasswordRecovery,
  verifyPasswordRecoveryCode,
} from '../services/passwordRecoveryService';
import '../styles/RecoverPasswordPage.css';

const logo3Bet = '/icones/icone-sem-fundo-com-trangulo.png';

const formatCpf = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

const formatCode = (value: string): string => value.replace(/\D/g, '').slice(0, 6);

type Step = 'request' | 'verify' | 'confirm' | 'done';

const RecoverPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('request');
  const [cpf, setCpf] = useState('');
  const [code, setCode] = useState('');
  const [recoveryToken, setRecoveryToken] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState<number | null>(null);

  const stepIndex = useMemo(() => {
    if (step === 'request') return 1;
    if (step === 'verify') return 2;
    if (step === 'confirm') return 3;
    return 4;
  }, [step]);

  useEffect(() => {
    if (!retryAfterSeconds || retryAfterSeconds <= 0) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setRetryAfterSeconds((current) => {
        if (!current || current <= 1) {
          window.clearInterval(interval);
          return null;
        }

        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(interval);
  }, [retryAfterSeconds]);

  const requestCooldownText = useMemo(() => {
    if (!retryAfterSeconds || retryAfterSeconds <= 0) {
      return null;
    }

    const minutes = Math.ceil(retryAfterSeconds / 60);
    const seconds = retryAfterSeconds % 60;

    if (minutes <= 1) {
      return `Aguarde ${Math.max(1, retryAfterSeconds)} segundo${retryAfterSeconds === 1 ? '' : 's'} para tentar novamente.`;
    }

    return `Aguarde ${minutes} minuto${minutes === 1 ? '' : 's'}${seconds > 0 ? ` e ${seconds} segundo${seconds === 1 ? '' : 's'}` : ''} para tentar novamente.`;
  }, [retryAfterSeconds]);

  const requestButtonLabel = useMemo(() => {
    if (loading) {
      return 'Enviando...';
    }

    if (retryAfterSeconds && retryAfterSeconds > 0) {
      const minutes = Math.floor(retryAfterSeconds / 60);
      const seconds = retryAfterSeconds % 60;

      if (minutes > 0) {
        return seconds > 0
          ? `Aguardar ${minutes}m ${seconds.toString().padStart(2, '0')}s`
          : `Aguardar ${minutes}m`;
      }

      return `Aguardar ${retryAfterSeconds}s`;
    }

    return 'Enviar codigo por SMS';
  }, [loading, retryAfterSeconds]);

  const handleRequest = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);

    try {
      setRetryAfterSeconds(null);
      const cleanCpf = cpf.replace(/\D/g, '');
      if (cleanCpf.length !== 11) {
        throw new Error('Informe um CPF valido.');
      }

      const result = await requestPasswordRecovery(cleanCpf);
      if (!result.sent) {
        throw new Error('Nao foi possivel iniciar a recuperacao agora.');
      }

      setCpf(formatCpf(cleanCpf));
      setCode('');
      setRecoveryToken('');
      setStep('verify');
      setMessage('Enviamos um codigo por SMS para o telefone cadastrado.');
    } catch (err: any) {
      const retryAfter = Number(err?.retryAfter || err?.retryAfterMinutes * 60);
      if (err?.status === 429 && Number.isFinite(retryAfter) && retryAfter > 0) {
        setRetryAfterSeconds(retryAfter);
      }
      setError(err?.message || 'Nao foi possivel iniciar a recuperacao agora.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);

    try {
      const cleanCpf = cpf.replace(/\D/g, '');
      const cleanCode = code.replace(/\D/g, '');
      if (cleanCode.length !== 6) {
        throw new Error('Informe o codigo de 6 digitos.');
      }

      const result = await verifyPasswordRecoveryCode(cleanCpf, cleanCode);
      setRecoveryToken(result.recoveryToken);
      setStep('confirm');
      setMessage('Codigo validado. Agora crie uma nova senha.');
    } catch (err: any) {
      setError(err?.message || 'Codigo invalido ou expirado.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);

    try {
      if (!recoveryToken) {
        throw new Error('Valide o codigo antes de criar uma nova senha.');
      }

      if (password.length < 8) {
        throw new Error('A senha precisa ter pelo menos 8 caracteres.');
      }

      if (password !== passwordConfirm) {
        throw new Error('As senhas nao conferem.');
      }

      await confirmPasswordRecovery(recoveryToken, password, passwordConfirm);
      setStep('done');
      setMessage('Senha redefinida com sucesso. Voce ja pode entrar novamente.');
    } catch (err: any) {
      setError(err?.message || 'Nao foi possivel redefinir a senha.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="wallet-recovery-page">
      <div className="wallet-recovery-shell">
        <div className="wallet-recovery-shell__noise" />
        <div className="wallet-recovery-shell__halo wallet-recovery-shell__halo--top" />
        <div className="wallet-recovery-shell__halo wallet-recovery-shell__halo--bottom" />

        <header className="wallet-recovery-brand">
          <img src={logo3Bet} alt="3BET Wallet" className="wallet-recovery-brand__logo" />
          <div className="wallet-recovery-brand__copy">
            <span>Recuperacao de senha</span>
            <h1>Retome o acesso com segurança</h1>
          </div>
        </header>

        <section className="wallet-recovery-card">
          <div className="wallet-recovery-stepper" aria-label="Progresso da recuperacao">
            <span className={stepIndex >= 1 ? 'is-active' : ''}>1 CPF</span>
            <span className={stepIndex >= 2 ? 'is-active' : ''}>2 Codigo</span>
            <span className={stepIndex >= 3 ? 'is-active' : ''}>3 Senha</span>
          </div>

          {message && (
            <div className="wallet-recovery-message" role="status" aria-live="polite">
              {message}
            </div>
          )}

          {error && (
            <div className="wallet-recovery-error" role="alert" aria-live="polite">
              {error}
            </div>
          )}

          {step === 'request' && (
            <form onSubmit={handleRequest} className="wallet-recovery-form">
              <label className="wallet-recovery-field">
                <span>CPF</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="username"
                  placeholder="000.000.000-00"
                  value={cpf}
                  onChange={(event) => setCpf(formatCpf(event.target.value))}
                  disabled={loading}
                />
              </label>

              <button
                type="submit"
                className={`wallet-recovery-primary ${
                  retryAfterSeconds && retryAfterSeconds > 0
                    ? 'wallet-recovery-primary--cooldown'
                    : ''
                }`}
                disabled={loading || Boolean(retryAfterSeconds && retryAfterSeconds > 0)}
              >
                <span className="wallet-recovery-primary__label">{requestButtonLabel}</span>
              </button>

              {requestCooldownText && (
                <div className="wallet-recovery-help" role="status" aria-live="polite">
                  {requestCooldownText}
                </div>
              )}
            </form>
          )}

          {step === 'verify' && (
            <form onSubmit={handleVerify} className="wallet-recovery-form">
              <label className="wallet-recovery-field">
                <span>CPF confirmado</span>
                <input type="text" value={cpf} disabled />
              </label>

              <label className="wallet-recovery-field">
                <span>Codigo de 6 digitos</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  value={code}
                  onChange={(event) => setCode(formatCode(event.target.value))}
                  disabled={loading}
                />
              </label>

              <button type="submit" className="wallet-recovery-primary" disabled={loading}>
                {loading ? 'Validando...' : 'Validar codigo'}
              </button>

              <button
                type="button"
                className="wallet-recovery-secondary"
                onClick={() => {
                  setCode('');
                  setRecoveryToken('');
                  setStep('request');
                }}
                disabled={loading}
              >
                Reenviar codigo
              </button>
            </form>
          )}

          {step === 'confirm' && (
            <form onSubmit={handleConfirm} className="wallet-recovery-form">
              <label className="wallet-recovery-field">
                <span>Nova senha</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  placeholder="Crie uma nova senha"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={loading}
                />
              </label>

              <label className="wallet-recovery-field">
                <span>Confirmar senha</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  placeholder="Repita a nova senha"
                  value={passwordConfirm}
                  onChange={(event) => setPasswordConfirm(event.target.value)}
                  disabled={loading}
                />
              </label>

              <button type="submit" className="wallet-recovery-primary" disabled={loading}>
                {loading ? 'Redefinindo...' : 'Redefinir senha'}
              </button>
            </form>
          )}

          {step === 'done' && (
            <div className="wallet-recovery-done">
              <button
                type="button"
                className="wallet-recovery-primary"
                onClick={() => navigate('/login')}
              >
                Voltar ao login
              </button>
            </div>
          )}

          <div className="wallet-recovery-footer">
            <Link to="/login">Voltar para o login</Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default RecoverPasswordPage;
